import {
  Alert,
  Anchor,
  Badge,
  Container,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { PiArrowLeft, PiInfo, PiNotePencil } from 'react-icons/pi';
import { Route, Switch, useLocation, useParams } from 'wouter';
import { formatDate } from '@/lib/helpers';
import { useHasPermission } from '@/models/permissions';
import { TReviewQueueRow, useReviewQueue } from '@/models/split-plan';
import SplitApprovalPanel from '@/pages/offers/payments/SplitApprovalPanel';
import { money } from '@/pages/offers/payments/splitDerive';
import { useSplitReviewData } from '@/pages/offers/payments/useSplitReviewData';
import { listUrl } from '@/lib/listMemory';

const APPROVE_SPLIT_PERM = 'offers:approve-split';

const statusMeta: Record<
  TReviewQueueRow['status'],
  { color: string; label: string }
> = {
  proposed: { color: 'orange', label: 'Awaiting review' },
  returned: { color: 'gray', label: 'Returned to SR' },
  approved: { color: 'teal', label: 'Approved' },
};

function StatusBadge({ status }: { status: TReviewQueueRow['status'] }) {
  const meta = statusMeta[status];
  return (
    <Badge size="sm" variant="light" color={meta.color}>
      {meta.label}
    </Badge>
  );
}

export default function SplitPlansPage() {
  const canApprove = useHasPermission(APPROVE_SPLIT_PERM);

  if (!canApprove) {
    return (
      <Paper p="md" h="100%">
        <Alert color="gray" icon={<PiInfo size={16} />} title="No access">
          <Text fz="sm">
            You need the split-plan review permission to view this page.
          </Text>
        </Alert>
      </Paper>
    );
  }

  return (
    <Container size="xl" py="md" w="100%">
      <Switch>
        <Route path="/:offerId" component={SplitReview} />
        <Route path="/" component={QueueList} />
      </Switch>
    </Container>
  );
}

function QueueList() {
  const [, nav] = useLocation();
  const { data, isLoading } = useReviewQueue();
  const plans = data?.plans ?? [];

  return (
    <Stack gap="md">
      <Text fw={600} fz="lg">
        Split Payment Plans
      </Text>

      <Table.ScrollContainer minWidth={1000}>
        <Table highlightOnHover verticalSpacing="sm" fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Offer</Table.Th>
              <Table.Th>SR</Table.Th>
              <Table.Th>Installments</Table.Th>
              <Table.Th>Total</Table.Th>
              <Table.Th>Services</Table.Th>
              <Table.Th>Updated</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {plans.map((p) => (
              <Table.Tr
                key={p.id}
                style={{ cursor: 'pointer' }}
                onClick={() => nav(`/${p.offerId}`)}
              >
                <Table.Td>
                  <StatusBadge status={p.status} />
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap" align="center">
                    <Stack gap={0}>
                      <Text fz="sm" fw={500}>
                        {p.offerTitle}
                      </Text>
                      <Text fz="xs" c="dimmed">
                        {p.accountName} · {p.contactName}
                      </Text>
                    </Stack>
                    {p.srNote && p.srNote.trim() && (
                      <Tooltip label={p.srNote} multiline maw={320} withArrow>
                        <Text
                          component="span"
                          c="grape"
                          style={{ lineHeight: 0 }}
                        >
                          <PiNotePencil size={16} />
                        </Text>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text fz="sm">{p.proposedByName}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="sm">{p.installments}</Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="sm">
                    {p.totalAmount != null
                      ? money(Number(p.totalAmount), p.currency)
                      : '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="sm">
                    {p.assignedLines}/{p.selectedLines}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz="xs">{formatDate(p.updatedAt)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && plans.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text fz="sm" c="dimmed" ta="center" py="md">
                    No split payment plans to review.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}

function SplitReview() {
  const params = useParams();
  const offerId = Number(params.offerId);
  const [, nav] = useLocation();
  const { data } = useReviewQueue();
  const row = data?.plans.find((p) => p.offerId === offerId);

  const {
    planData,
    isLoading,
    services,
    currency,
    accountId,
    itemsDraft,
    derived,
    orderNetMinor,
  } = useSplitReviewData(offerId, true);

  const planStatus = planData?.plan?.status;
  const status: TReviewQueueRow['status'] | null =
    row?.status ??
    (planStatus === 'proposed' ||
    planStatus === 'returned' ||
    planStatus === 'approved'
      ? planStatus
      : null);

  return (
    <Stack gap="md">
      <Anchor
        fz="sm"
        onClick={() => nav(`~${listUrl('/split-plans')}`)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <PiArrowLeft size={14} /> Back to queue
      </Anchor>

      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Stack gap={2}>
            <Text fw={600} fz="xl">
              {row?.offerTitle ?? `Offer #${offerId}`}
            </Text>
            {row && (
              <Text fz="sm" c="dimmed">
                {row.accountName} · {row.contactName} · SR {row.proposedByName}
              </Text>
            )}
          </Stack>
          {status && <StatusBadge status={status} />}
        </Group>
      </Paper>

      {isLoading && (
        <Text fz="sm" c="dimmed">
          Loading schedule…
        </Text>
      )}

      {status === 'proposed' && planData?.plan ? (
        <SplitApprovalPanel
          offerId={offerId}
          currency={currency}
          installments={itemsDraft}
          services={services}
          accountId={accountId}
          items={planData.items}
          srNote={planData.plan.srNote}
          orderNetMinor={orderNetMinor}
        />
      ) : (
        status && (
          <ReadOnlySummary
            row={row}
            currency={currency}
            derived={derived}
            installments={itemsDraft.length}
          />
        )
      )}
    </Stack>
  );
}

function ReadOnlySummary({
  row,
  currency,
  derived,
  installments,
}: {
  row?: TReviewQueueRow;
  currency: string;
  derived: ReturnType<typeof useSplitReviewData>['derived'];
  installments: number;
}) {
  const statusText =
    row?.status === 'returned'
      ? 'This schedule was returned to the SR for revision.'
      : 'This schedule has been approved.';

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Text fz="sm" c="dimmed">
          {statusText}
        </Text>
        <Group gap="xl" wrap="wrap">
          <Stack gap={0}>
            <Text fz="xs" c="dimmed">
              Installments
            </Text>
            <Text fz="sm" fw={500}>
              {installments}
            </Text>
          </Stack>
          <Stack gap={0}>
            <Text fz="xs" c="dimmed">
              Total
            </Text>
            <Text fz="sm" fw={500}>
              {money(derived.orderedTotal, currency)}
            </Text>
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
}
